import {
  computed,
  onBeforeUnmount,
  onMounted,
  ref,
  shallowRef,
  type ComputedRef,
  type Ref,
} from 'vue'

export interface UseElementFullscreen {
  isFullscreen: ComputedRef<boolean>
  isFallback: ComputedRef<boolean>
  error: Ref<unknown>
  toggle: () => Promise<void>
}

/** Native element fullscreen with a CSS fallback for environments that do not expose the API.
 * The fallback is deliberately local to the report surface and remains keyboard-dismissible. */
export function useElementFullscreen(target: Ref<HTMLElement | null>): UseElementFullscreen {
  const active = ref(false)
  const fallback = ref(false)
  const error = shallowRef<unknown>(null)
  let previousBodyOverflow: string | null = null

  function syncNativeState(): void {
    if (fallback.value) return
    active.value = document.fullscreenElement === target.value
  }

  function leaveFallback(): void {
    fallback.value = false
    active.value = false
    if (previousBodyOverflow !== null) {
      document.body.style.overflow = previousBodyOverflow
      previousBodyOverflow = null
    }
  }

  function onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape' && fallback.value) leaveFallback()
  }

  async function enter(): Promise<void> {
    const element = target.value
    if (!element) return
    error.value = null
    if (typeof element.requestFullscreen !== 'function') {
      previousBodyOverflow = document.body.style.overflow
      document.body.style.overflow = 'hidden'
      fallback.value = true
      active.value = true
      return
    }
    try {
      await element.requestFullscreen()
      active.value = document.fullscreenElement === element
    } catch (cause) {
      error.value = cause
      active.value = false
    }
  }

  async function leave(): Promise<void> {
    error.value = null
    if (fallback.value) {
      leaveFallback()
      return
    }
    if (document.fullscreenElement && typeof document.exitFullscreen === 'function') {
      try {
        await document.exitFullscreen()
      } catch (cause) {
        error.value = cause
      }
    }
    syncNativeState()
  }

  async function toggle(): Promise<void> {
    if (active.value) await leave()
    else await enter()
  }

  onMounted(() => {
    document.addEventListener('fullscreenchange', syncNativeState)
    document.addEventListener('keydown', onKeydown)
    syncNativeState()
  })
  onBeforeUnmount(() => {
    if (fallback.value) leaveFallback()
    document.removeEventListener('fullscreenchange', syncNativeState)
    document.removeEventListener('keydown', onKeydown)
  })

  return {
    isFullscreen: computed(() => active.value),
    isFallback: computed(() => fallback.value),
    error,
    toggle,
  }
}
